import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { ThemeMode } from '@astral/shared';
import {
  getTheme,
  resolveScheme,
  themeToCssVars,
  type ColorScheme,
} from '@astral/theme';
import { applyTelegramChrome, getColorScheme } from '@astral/telegram/webapp';

interface ThemeContextValue {
  scheme: ColorScheme;
  themeId: string;
  mode: ThemeMode;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export interface ThemeProviderProps {
  themeId: string;
  mode: ThemeMode;
  children: ReactNode;
}

/** Detect the system color scheme, preferring Telegram's when available. */
function detectSystemScheme(): ColorScheme {
  const telegram = getColorScheme();
  if (telegram) return telegram;
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
  }
  return 'dark';
}

/**
 * Resolves the active theme + color scheme and applies it as CSS custom
 * properties on the document root. Re-applies whenever the user changes theme
 * or mode, and follows the system scheme when mode is "auto".
 */
export function ThemeProvider({ themeId, mode, children }: ThemeProviderProps): JSX.Element {
  const [systemScheme, setSystemScheme] = useState<ColorScheme>(detectSystemScheme);

  // Keep following the OS scheme while in auto/custom mode.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const query = window.matchMedia('(prefers-color-scheme: light)');
    const onChange = (): void => setSystemScheme(detectSystemScheme());
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  const scheme = resolveScheme(mode, systemScheme);
  const theme = getTheme(themeId);

  useEffect(() => {
    const vars = themeToCssVars(theme, scheme);
    const root = document.documentElement;
    for (const [name, value] of Object.entries(vars)) {
      root.style.setProperty(name, value);
    }
    root.dataset.scheme = scheme;
    root.style.colorScheme = scheme;
    // Sync the Telegram header/background to the app background.
    const background = theme.schemes[scheme].background;
    applyTelegramChrome(background, background);
  }, [theme, scheme]);

  const value = useMemo<ThemeContextValue>(
    () => ({ scheme, themeId, mode }),
    [scheme, themeId, mode],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within a ThemeProvider');
  return ctx;
}
